import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Briefcase, Building2, CheckCircle2, Clock, Users, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDate, fmtDateTime } from "@/lib/dates";
export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefDays, setBriefDays] = useState(7);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/analytics/summary");
        setData(r.data);
      } catch (e) { console.error("analytics/summary load failed:", e); }
    })();
    (async () => {
      try {
        const r = await api.get("/analytics/workload");
        setWorkload(r.data);
      } catch (e) { console.error("analytics/workload load failed:", e); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/firm-brief", { params: { days: briefDays } });
        setBrief(r.data);
      } catch (e) { console.error("admin/firm-brief load failed:", e); }
    })();
  }, [briefDays]);

  const sendBrief = async () => {
    setSending(true);
    try {
      await api.post("/admin/send-firm-brief");
      toast.success("Firm Brief sent to all Admins");
    } catch { toast.error("Failed to send"); }
    setSending(false);
  };

  if (!data) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const totalProjects = Object.values(data.projects_by_status || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-white" data-testid="analytics-page">
      <PageHeader title="Analytics" subtitle="Firm-wide insights" />

      <div className="px-4 sm:px-8 py-6 space-y-8">
        {/* Firm Brief */}
        {brief && (
          <Card className="rounded-sm border-slate-200 shadow-none p-5" data-testid="card-firm-brief">
            <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className="gc-label">Firm Brief — last {brief.days} days</div>
                <div className="text-[11px] text-slate-500 font-mono-data">{brief.since} → {brief.to}</div>
              </div>
              <div className="flex items-center gap-2">
                <Select value={String(briefDays)} onValueChange={(v) => setBriefDays(parseInt(v, 10))}>
                  <SelectTrigger className="w-32 rounded-sm h-9" data-testid="brief-days"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                  </SelectContent>
                </Select>
                {user.role === "Admin" && (
                  <Button onClick={sendBrief} disabled={sending} className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm h-9" data-testid="btn-send-brief">
                    <Send size={13} className="mr-1.5" /> {sending ? "Sending…" : "Send to Admins"}
                  </Button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <BriefStat label="New Clients" value={brief.new_clients} />
              <BriefStat label="New Projects" value={brief.new_projects_count} />
              <BriefStat label="Completed" value={brief.completed_projects_count} accent="green" />
              <BriefStat label="Stuck" value={brief.stuck_projects.length} accent="amber" />
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
              <div>
                <div className="gc-label mb-1">Compliance Due Next {brief.days}d</div>
                <ul className="space-y-1 text-[11px]">
                  {brief.compliance_upcoming.length === 0 && <li className="text-slate-400">—</li>}
                  {brief.compliance_upcoming.slice(0, 4).map((c) => (
                    <li key={`${c.due_date}-${c.title}`}><span className="font-mono-data text-[#0a2540]">{fmtDate(c.due_date)}</span> · {c.client_name} · {c.title}</li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="gc-label mb-1">Leaves Approved</div>
                <ul className="space-y-1 text-[11px]">
                  {brief.leaves_approved.length === 0 && <li className="text-slate-400">—</li>}
                  {brief.leaves_approved.slice(0, 4).map((l) => (
                    <li key={`${l.user_name}-${l.leave_type}-${l.days}`}>{l.user_name} · {l.leave_type} ({l.days}d)</li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="mt-3 text-[10px] text-slate-400">Automated digest sent every Monday 9 AM to all Admins.</p>
          </Card>
        )}

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Clients" value={data.counts.clients} icon={Building2} />
          <Kpi label="Projects" value={data.counts.projects} icon={Briefcase} />
          <Kpi label="Open Tasks" value={data.counts.tasks_open} icon={Clock} />
          <Kpi label="Active Users" value={data.counts.users} icon={Users} />
          <Kpi label="Pending Leaves" value={data.counts.leaves_pending} icon={AlertTriangle} />
        </div>

        {/* Projects by status + Top stuck */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="rounded-sm border-slate-200 shadow-none p-5" data-testid="card-projects-by-status">
            <div className="gc-label">Projects by Status</div>
            <div className="mt-4 space-y-3">
              {Object.entries(data.projects_by_status).map(([k, v]) => {
                const pct = totalProjects > 0 ? Math.round((v * 100) / totalProjects) : 0;
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <div className="flex items-center gap-2"><StatusBadge status={k} /></div>
                      <div className="font-mono-data text-[#0a2540]">{v} <span className="text-slate-400">· {pct}%</span></div>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden">
                      <div className="h-full bg-[#0a2540]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="rounded-sm border-slate-200 shadow-none p-5" data-testid="card-top-stuck">
            <div className="gc-label">Top Stuck Clients</div>
            <div className="mt-4 space-y-2">
              {(data.top_stuck_clients || []).length === 0 && <div className="text-xs text-slate-400">No stuck projects</div>}
              {(data.top_stuck_clients || []).map((c) => (
                <Link to={`/clients/${c.client_id}`} key={c.client_id} className="flex items-center justify-between text-xs px-3 py-2 border border-slate-200 hover:border-[#0a2540]">
                  <span className="truncate">{c.client_name}</span>
                  <span className="font-mono-data text-amber-700">{c.count}</span>
                </Link>
              ))}
            </div>
          </Card>
        </div>

        {/* Team Workload */}
        <Card className="rounded-sm border-slate-200 shadow-none p-5" data-testid="card-workload">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div>
              <div className="gc-label">Team Workload</div>
              <p className="text-[11px] text-slate-500 mt-1">Open tasks per teammate — split by priority, overdue, and stuck. Sorted by overdue first.</p>
            </div>
            {workload?.as_of && (
              <div className="text-[10px] text-slate-400 font-mono-data">As of {workload.as_of}</div>
            )}
          </div>
          <WorkloadTable rows={workload?.rows || []} loading={workload === null} />
        </Card>

        {/* Leave by category */}
        <Card className="rounded-sm border-slate-200 shadow-none p-5" data-testid="card-leave-by-cat">
          <div className="gc-label">Leave Usage by Category (this year)</div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(data.leave_by_category || {}).length === 0 && (
              <div className="col-span-full text-xs text-slate-400">No approved leaves yet this year</div>
            )}
            {Object.entries(data.leave_by_category || {}).map(([k, v]) => (
              <div key={k} className="border border-slate-200 px-3 py-2">
                <div className="gc-label text-[10px]">{k}</div>
                <div className="text-2xl font-mono-data font-medium text-[#0a2540]">{v}</div>
                <div className="text-[10px] text-slate-400">days approved</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Attendance heatmap (last 30 days) */}
        <Card className="rounded-sm border-slate-200 shadow-none p-5" data-testid="card-attendance-heatmap">
          <div className="gc-label">Attendance — last 30 days</div>
          <AttendanceHeatmap data={data.attendance_heatmap || {}} />
        </Card>

        {/* Compliance upcoming */}
        <Card className="rounded-sm border-slate-200 shadow-none p-5" data-testid="card-compliance-upcoming">
          <div className="gc-label">Compliance Items Due — next 30 days</div>
          <div className="mt-4 border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2">Due</th>
                  <th className="text-left px-4 py-2">Client</th>
                  <th className="text-left px-4 py-2">Database</th>
                  <th className="text-left px-4 py-2">Item</th>
                  <th className="text-left px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {(data.compliance_upcoming_30d || []).length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-xs text-slate-400">Nothing due in the next 30 days</td></tr>
                )}
                {(data.compliance_upcoming_30d || []).map((it) => (
                  <tr key={it.item_id} className="border-b border-slate-100" data-testid={`compliance-due-${it.item_id}`}>
                    <td className="px-4 py-2 font-mono-data text-xs text-[#0a2540]">{fmtDate(it.due_date)}</td>
                    <td className="px-4 py-2 text-xs">
                      <Link to={`/clients/${it.client_id}`} className="text-[#0a2540] hover:underline">{it.client_name}</Link>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">{it.db_name}</td>
                    <td className="px-4 py-2 text-xs">{it.title}</td>
                    <td className="px-4 py-2 text-xs">{it.status || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function BriefStat({ label, value, accent }) {
  const color = accent === "green" ? "text-green-700" : accent === "amber" ? "text-amber-700" : "text-[#0a2540]";
  return (
    <div className="bg-slate-50 px-3 py-2 border border-slate-200 rounded-sm">
      <div className="gc-label text-[10px]">{label}</div>
      <div className={`text-xl font-mono-data font-medium ${color}`}>{value}</div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon }) {
  return (
    <div className="border border-slate-200 px-4 py-3 flex items-center justify-between gap-3" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div>
        <div className="gc-label text-[10px]">{label}</div>
        <div className="text-2xl font-mono-data font-medium text-[#0a2540]">{value}</div>
      </div>
      <Icon size={18} className="text-slate-300" />
    </div>
  );
}

function WorkloadTable({ rows, loading }) {
  if (loading) {
    return <div className="mt-4 text-xs text-slate-400">Loading workload…</div>;
  }
  if (rows.length === 0) {
    return <div className="mt-4 text-xs text-slate-400">No active teammates</div>;
  }
  const maxOpen = Math.max(1, ...rows.map((r) => r.open));
  return (
    <div className="mt-4 border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm" data-testid="workload-table">
        <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left px-4 py-2">Teammate</th>
            <th className="text-left px-4 py-2 hidden sm:table-cell">Load</th>
            <th className="text-right px-3 py-2">Open</th>
            <th className="text-right px-3 py-2"><span className="text-red-700">High</span></th>
            <th className="text-right px-3 py-2 hidden md:table-cell">Med</th>
            <th className="text-right px-3 py-2 hidden md:table-cell">Low</th>
            <th className="text-right px-3 py-2"><span className="text-red-700">Overdue</span></th>
            <th className="text-right px-3 py-2 hidden md:table-cell"><span className="text-amber-700">Stuck</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pct = (r.open * 100) / maxOpen;
            const isIdle = r.open === 0;
            return (
              <tr key={r.user_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`workload-row-${r.user_id}`}>
                <td className="px-4 py-2.5">
                  <div className="text-xs font-medium text-[#0a2540] truncate">{r.name}</div>
                  <div className="text-[10px] text-slate-400 font-mono-data truncate">{r.role}{r.email ? ` · ${r.email}` : ""}</div>
                </td>
                <td className="px-4 py-2.5 hidden sm:table-cell">
                  <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden w-32">
                    <div
                      className="h-full"
                      style={{
                        width: `${pct}%`,
                        background: r.overdue > 0 ? "#b91c1c" : r.high > 0 ? "#0a2540" : "#64748b",
                      }}
                    />
                  </div>
                  {isIdle && <div className="text-[10px] text-slate-400 mt-1">Idle</div>}
                </td>
                <td className="px-3 py-2.5 text-right font-mono-data text-xs text-[#0a2540]">{r.open}</td>
                <td className="px-3 py-2.5 text-right font-mono-data text-xs">
                  {r.high > 0 ? <span className="text-red-700">{r.high}</span> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right font-mono-data text-xs hidden md:table-cell text-slate-600">{r.medium || <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2.5 text-right font-mono-data text-xs hidden md:table-cell text-slate-600">{r.low || <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-2.5 text-right font-mono-data text-xs">
                  {r.overdue > 0 ? <span className="text-red-700 font-semibold">{r.overdue}</span> : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right font-mono-data text-xs hidden md:table-cell">
                  {r.stuck > 0 ? <span className="text-amber-700">{r.stuck}</span> : <span className="text-slate-300">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AttendanceHeatmap({ data }) {
  const days = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    days.push({ date: iso, count: data[iso] || 0 });
  }
  const max = Math.max(1, ...days.map((d) => d.count));
  return (
    <div className="mt-4">
      <div className="grid grid-cols-10 md:grid-cols-15 gap-1.5">
        {days.map((d) => {
          const intensity = d.count === 0 ? 0 : Math.min(1, d.count / max);
          const bg = d.count === 0
            ? "#f1f5f9"
            : `rgba(10, 37, 64, ${0.25 + 0.75 * intensity})`;
          return (
            <div
              key={d.date}
              title={`${d.date}: ${d.count} present`}
              className="h-6 rounded-sm border border-slate-100"
              style={{ background: bg }}
              data-testid={`heat-${d.date}`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400 font-mono-data">
        <span>{days[0]?.date}</span>
        <span>Today</span>
      </div>
    </div>
  );
}
